import { hasAdminAccess } from './admin';

/**
 * Frontend mirror of the worker's `resolveUserPlan`.
 *
 * Historically tier reads were scattered across pages, each reading a
 * different single field (`user.metadata.plan` here, `user.tier` there),
 * and paid users whose tier landed on the *other* field showed up as
 * `free` — the exact bug that locked PDF export for paid users on
 * ProjectDetail. This helper looks in every place the tier is
 * legitimately stored, plus falls back to a live `subscription_status`
 * of `active` / `trialing`, and treats admins as always-paid.
 */

export type PlanTier = 'free' | 'pro' | 'enterprise';

interface MinimalUser {
  id?: string;
  email?: string;
  role?: string | null;
  tier?: string | null;
  plan?: string | null;
  subscription_status?: string | null;
  resolvedPlan?: string | null; // /sdk/auth/me now returns this
  metadata?: {
    tier?: string | null;
    plan?: string | null;
    [key: string]: unknown;
  } | null;
}

const PAID: PlanTier[] = ['pro', 'enterprise'];

export function resolveUserTier(user?: MinimalUser | null): PlanTier {
  if (!user) return 'free';

  // 1. If admin (via users.role or hardcoded allowlist), treat as
  //    enterprise. Admins bypass paywalls.
  if (hasAdminAccess(user.id, user.email, user.role || undefined)) return 'enterprise';

  // 2. Server-computed tier from /sdk/auth/me — most authoritative.
  const server = normalise(user.resolvedPlan);
  if (server) return server;

  // 3. Every field that has historically held a tier value.
  const candidates = [
    user.tier,
    user.metadata?.tier,
    user.metadata?.plan,
    user.plan,
  ]
    .map(normalise)
    .filter(Boolean) as PlanTier[];

  const paidFirst = candidates.find((v) => PAID.includes(v));
  if (paidFirst) return paidFirst;

  // 4. Active subscription trumps a stale tier field.
  const status = String(user.subscription_status || '').toLowerCase();
  if (['active', 'trialing'].includes(status)) return 'pro';

  return candidates[0] || 'free';
}

export function isPaidUser(user?: MinimalUser | null): boolean {
  return PAID.includes(resolveUserTier(user));
}

function normalise(value: unknown): PlanTier | '' {
  const v = String(value ?? '').toLowerCase().trim();
  if (v === 'pro' || v === 'enterprise' || v === 'free') return v;
  return '';
}
